"use client";

import { useEffect, useState } from "react";

type SupportMatch = {
  window: number;
  ma: number;
  dist_pct: number;
  ma_type: string;
};

type SupportRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  matches: SupportMatch[];
};

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

export default function SupportScannerPage() {
  const [maType, setMaType] = useState("SMA");
  const [maPeriod, setMaPeriod] = useState("50");
  const [thresholdPct, setThresholdPct] = useState("2");
  const [rows, setRows] = useState<SupportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const isDefault = maType === "SMA" && maPeriod === "50" && String(thresholdPct) === "2";
        const params = new URLSearchParams();
        params.set("ma_type", maType);
        params.set("windows", maPeriod);
        params.set("threshold_pct", String(thresholdPct));
        params.set("limit", "20");
        const url = isDefault
          ? "/api/outputs/scanner_ma.json"
          : `/api/scanner/supports?${params.toString()}`;
        const res = await fetch(url);
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.error ?? json.details ?? `HTTP ${res.status}`);
        }
        setRows(Array.isArray(json.rows) ? json.rows : []);
      } catch (err) {
        setRows([]);
        setError(err instanceof Error ? err.message : "Failed to load support scan.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [maType, maPeriod, thresholdPct]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">EMA/SMA Support Scanner</h1>
          <p className="page-subtitle">Near selected moving average support (top 20 by volume)</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1.5rem", 
            marginBottom: "2rem",
            background: "rgba(15, 23, 42, 0.4)",
            padding: "1.5rem",
            borderRadius: "12px",
            border: "1px solid rgba(148, 163, 184, 0.15)"
          }}>
            {/* MA Type Selector */}
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--blue-text)" }}>
                Moving Average Type
              </label>
              <select
                value={maType}
                onChange={(e) => setMaType(e.target.value)}
                style={{ 
                  width: "100%",
                  padding: "0.75rem", 
                  borderRadius: "8px", 
                  border: "1px solid var(--color-border)", 
                  background: "rgba(255,255,255,0.05)", 
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500
                }}
              >
                <option value="SMA">SMA (Simple Moving Average)</option>
                <option value="EMA">EMA (Exponential Moving Average)</option>
              </select>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.4rem" }}>
                SMA gives equal weight to all prices. EMA emphasizes recent prices.
              </p>
            </div>

            {/* Period Selector */}
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--blue-text)" }}>
                Period (Lookback Days)
              </label>
              <select
                value={maPeriod}
                onChange={(e) => setMaPeriod(e.target.value)}
                style={{ 
                  width: "100%",
                  padding: "0.75rem", 
                  borderRadius: "8px", 
                  border: "1px solid var(--color-border)", 
                  background: "rgba(255,255,255,0.05)", 
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500
                }}
              >
                {["10", "20", "50", "100", "200"].map((w) => (
                  <option key={w} value={w}>{w} days</option>
                ))}
              </select>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.4rem" }}>
                Longer periods = stronger, more stable support levels
              </p>
            </div>

            {/* Threshold Selector */}
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--blue-text)" }}>
                Proximity Threshold (%)
              </label>
              <select
                value={thresholdPct}
                onChange={(e) => setThresholdPct(e.target.value)}
                style={{ 
                  width: "100%",
                  padding: "0.75rem", 
                  borderRadius: "8px", 
                  border: "1px solid var(--color-border)", 
                  background: "rgba(255,255,255,0.05)", 
                  color: "var(--color-text)",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  boxSizing: "border-box"
                }}
              >
                <option value="1">1% (Tight)</option>
                <option value="2">2% (Standard)</option>
                <option value="3">3% (Relaxed)</option>
              </select>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.4rem" }}>
                Show shares within ±{thresholdPct}% of the moving average
              </p>
            </div>
          </div>

          <div className="results-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(138, 180, 248, 0.18)", borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Symbol</th>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Company</th>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Sector</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>Close</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>MA Value</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>Distance %</th>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Support</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      Loading support scan...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={8} style={{ padding: "1rem", color: "#fca5a5" }}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && rows.map((r, idx) => {
                  const firstMatch = r.matches[0];
                  return (
                    <tr key={`${r.symbol}-${idx}`} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "0.85rem", fontWeight: 600 }}>{r.symbol}</td>
                      <td style={{ padding: "0.85rem", color: "var(--color-text-muted)" }}>{r.name}</td>
                      <td style={{ padding: "0.85rem", color: "var(--color-text-muted)" }}>{r.sector}</td>
                      <td style={{ padding: "0.85rem", textAlign: "right", fontWeight: 600 }}>{formatNumber(r.close)}</td>
                      <td style={{ padding: "0.85rem", textAlign: "right", color: "#a78bfa", fontWeight: 500 }}>
                        {firstMatch ? formatNumber(firstMatch.ma) : "—"}
                      </td>
                      <td style={{ 
                        padding: "0.85rem", 
                        textAlign: "right",
                        color: firstMatch && Math.abs(firstMatch.dist_pct) < 1 ? "var(--color-success)" : "var(--color-text-muted)"
                      }}>
                        {firstMatch ? formatNumber(Math.abs(firstMatch.dist_pct), 2) + "%" : "—"}
                      </td>
                      <td style={{ padding: "0.85rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                        {r.matches.map((m) => `${m.ma_type}${m.window}`).join(", ")}
                      </td>
                      <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>{r.date}</td>
                    </tr>
                  );
                })}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      No matches.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
